import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../../../../ui/components/Button";
import { Select } from "../../../../ui/components/Select";
import { TextField } from "../../../../ui/components/TextField";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { useVendorOperations } from "../hooks";
import { VendorEntity } from "../../domain/entities/VendorEntity";
import {
  fetchCurrencies,
  Currency,
} from "../../../../shared/data/api/currenciesApi";
import { fetchTDSRates, TDSRate } from "../../../../shared/data/api/taxApi";
import {
  formatTdsRateLabel,
  sortTdsRatesForPartyForm,
} from "../../../../shared/utils/tds";
import {
  SearchableDropdown,
  ErrorBanner,
  useToast,
  ReviewRequestModal,
} from "../../../../shared/presentation/components";
import {
  useErrorHandling,
  usePermissions,
} from "../../../../shared/presentation/hooks";
import {
  MODULE_CODES,
  ACTION_CODES,
} from "../../../../shared/constants/permissions";
import { FeatherChevronDown } from "@subframe/core";
import { getStoredCompanyData } from "../../../../services/api";
import VendorContextSection from "../../../../shared/presentation/components/VendorContextSection";
import {
  VendorContextData,
  saveVendorContext,
} from "../../../../services/contextApi";
import GstinVerificationField from "../../../../shared/presentation/components/GstinVerificationField";
import GSTINPrefillDialog from "../../../../components/GSTINPrefillDialog";
import { useGstinVerification } from "../../../../shared/presentation/hooks/useGstinVerification";
import { getCompany } from "../../../../shared/data/storage/authStorage";
import { getStateAbbreviation } from "../../../../utils";
import {
  buildManualBillVendorPatch,
  isCreateManualBillReturnState,
} from "../utils/manualBillVendorPatch";

import {
  COUNTRIES,
  findCountryByCode,
} from "../../../../shared/constants/countries";
import {
  INDIAN_STATES,
  findIndianStateByCode,
} from "../../../../shared/constants/indianStates";
import { getCurrencyIdForCountry } from "../../../../shared/utils/countryCurrency";
import {
  buildVendorGstinPrefillFields,
  buildVendorGstinPrefillUpdates,
} from "../../../../shared/utils/vendorGstinPrefill";

const AddNewPartyPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const manualBillReturn = isCreateManualBillReturnState(location.state)
    ? location.state
    : null;

  // Use hooks
  const { createVendor, loading: isCreating } = useVendorOperations();

  // Form state
  const [formData, setFormData] = useState<Partial<VendorEntity>>({
    // invoice_submission_method: "email",
    msme_flag: "N", // Default to "no" for non-India countries
  });

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(true);
  const [currenciesError, setCurrenciesError] = useState<string | null>(null);

  // TDS rates state
  const [tdsRates, setTdsRates] = useState<TDSRate[]>([]);
  const [tdsRatesLoading, setTdsRatesLoading] = useState(true);
  const [tdsRatesError, setTdsRatesError] = useState<string | null>(null);

  // Vendor type state (vendor_lite or vendor_pro)
  const [vendorType, setVendorType] = useState<"vendor_lite" | "vendor_pro">(
    "vendor_lite",
  );

  // Review workflow state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<Partial<VendorEntity> | null>(null);

  // Vendor context state
  const [vendorContextData, setVendorContextData] = useState<VendorContextData>(
    {},
  );

  // Error handling with new hook
  const { errors, setFieldErrors, setApiErrors, clearErrors, clearFieldError } =
    useErrorHandling();

  // Toast notifications
  const { showSuccess } = useToast();

  // Permission hooks
  const { hasPermission, requiresReview } = usePermissions();
  const canCreate = hasPermission(MODULE_CODES.VENDOR, ACTION_CODES.CREATE);
  const needsReview = requiresReview(MODULE_CODES.VENDOR, ACTION_CODES.CREATE);

  // GSTIN verification
  const gstinVerification = useGstinVerification({
    entityName: (formData.vendor_legal_name as string) || "",
    entityType: "vendor",
  });

  // Prefill dialog state
  const [showPrefillDialog, setShowPrefillDialog] = useState(false);

  // Show prefill dialog when GSTIN verification succeeds
  useEffect(() => {
    if (
      gstinVerification.status === "verified" &&
      gstinVerification.verificationData?.status === "Active"
    ) {
      setShowPrefillDialog(true);
    }
  }, [gstinVerification.status, gstinVerification.verificationData]);

  // Fetch currencies on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        setCurrenciesLoading(true);
        setCurrenciesError(null);
        const response = await fetchCurrencies();
        setCurrencies(response.data);
      } catch (error) {
        console.error("Error fetching currencies:", error);
        setCurrenciesError("Failed to load currencies");
      } finally {
        setCurrenciesLoading(false);
      }
    };

    loadCurrencies();
  }, []);

  // Fetch TDS rates on component mount
  useEffect(() => {
    const loadTDSRates = async () => {
      try {
        setTdsRatesLoading(true);
        setTdsRatesError(null);
        const response = await fetchTDSRates();
        setTdsRates(response.data.tds_rates);
      } catch (error) {
        console.error("Error fetching TDS rates:", error);
        setTdsRatesError("Failed to load TDS rates");
      } finally {
        setTdsRatesLoading(false);
      }
    };

    loadTDSRates();
  }, []);

  // Fetch vendor_type from company profile on component mount
  useEffect(() => {
    const companyData = getStoredCompanyData();
    if (companyData && companyData.vendor_type) {
      setVendorType(companyData.vendor_type);
    }
  }, []);

  // Filter TDS rates: active first, expired at bottom
  const getFilteredTDSRates = () => {
    return sortTdsRatesForPartyForm(
      tdsRates.map((rate) => ({
        ...rate,
        is_expired: rate.is_expired ?? true,
      })),
    );
  };

  const handleApplyGstinPrefill = () => {
    const updates = buildVendorGstinPrefillUpdates(
      gstinVerification.verificationData,
      formData.gstin || "",
      currencies,
    );
    setFormData((prev) => ({ ...prev, ...updates }));
    (
      [
        "vendor_legal_name",
        "registered_office_address",
        "state",
        "city",
        "postal_code",
        "country",
        "pan_tax_id",
        "currency_id",
      ] as const
    ).forEach(clearFieldError);
    setShowPrefillDialog(false);
  };

  const handleInputChange = (
    field: keyof VendorEntity,
    value: string | number,
  ) => {
    if (field === "tds_tax_id") {
      const normalized =
        value === "" ? undefined : (value as VendorEntity["tds_tax_id"]);
      setFormData((prev) => {
        const cur = prev.tds_tax_id;
        if (
          cur === normalized ||
          (cur !== undefined &&
            normalized !== undefined &&
            String(cur) === String(normalized))
        ) {
          return prev;
        }
        return { ...prev, tds_tax_id: normalized };
      });
      clearFieldError("tds_tax_id");
      return;
    }

    if (field === "tds_applicable") {
      setFormData((prev) => {
        if (prev.tds_applicable === value) {
          return prev;
        }
        const newData: Partial<VendorEntity> = {
          ...prev,
          tds_applicable: value as string,
        };
        if (value === "N") {
          newData.tds_tax_id = undefined;
        }
        return newData;
      });
      clearFieldError("tds_applicable");
      if (value === "N") {
        clearFieldError("tds_tax_id");
      }
      return;
    }

    setFormData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Handle country change
      if (field === "country") {
        if (value !== "IN") {
          // Non-India country
          newData.msme_flag = "N";
          newData.udyam_number = undefined;
          // Clear TDS fields for non-India countries
          newData.tds_applicable = undefined;
          newData.tds_tax_id = undefined;
          newData.state = undefined;
          newData.gst_treatment = "overseas";
          // If GST treatment is overseas, set GST category to RCM
          // if (newData.type_of_vendor === "service") {
          newData.gst_category = "rcm";
          // }
        }
        // Note: For India, TDS Applicable can be "Y" or "N" - user can select

        const currencyId = getCurrencyIdForCountry(value as string, currencies);
        if (currencyId) {
          newData.currency_id = currencyId;
        }
      }

      // Handle GST treatment change
      if (field === "gst_treatment") {
        // Clear GSTIN when GST treatment is not Business GST
        if (value !== "business_gst") {
          newData.gstin = undefined;
          gstinVerification.reset();
        }

        // If country is not India and GST treatment is overseas, set category to RCM
        if (newData.country !== "IN" && value === "overseas") {
          newData.gst_category = "rcm";
        }
      }

      // Reset GSTIN verification when GSTIN changes
      if (field === "gstin") {
        gstinVerification.reset();
      }

      // Clear udyam_number when MSME is set to "no"
      if (field === "msme_flag" && value === "N") {
        newData.udyam_number = undefined;
      }

      return newData;
    });

    // Clear field error when user starts typing
    clearFieldError(field);
  };

  // Helper function to get user-friendly field names
  const getFieldName = (field: string): string => {
    const fieldNames: Record<string, string> = {
      vendor_legal_name: "Vendor Legal Name",
      vendor_legal_entity: "Vendor Legal Entity",
      type_of_vendor: "Type of Vendor",
      business_category: "Business Category",
      registered_office_address: "Registered Office Address",
      country: "Country",
      state: "State",
      city: "City",
      postal_code: "Postal Code",
      primary_contact_name: "Primary Contact Name",
      primary_contact_phone: "Primary Contact Phone",
      primary_contact_email: "Primary Contact Email",
      pan_tax_id: "PAN ID",
      gstin: "GSTIN",
      msme_flag: "MSME Flag",
      company_registration_number: "CIN",
      udyam_number: "Udyam Number",
      beneficiary_name: "Beneficiary Name",
      bank_account_number: "Bank Account Number",
      bank_name: "Bank Name",
      branch: "Branch",
      ifsc_swift_code: "IFSC/SWIFT Code",
      payment_terms: "Payment Terms",
      currency_id: "Currency",
      tds_applicable: "TDS Applicable",
      tds_tax_id: "TDS Tax Rate",
      tds_tax_rate: "TDS Tax Rate",
      gst_category: "GST Category",
      gst_treatment: "GST Treatment",
    };
    return fieldNames[field] || field.replace(/_/g, " ");
  };

  // Determine if vendor is lite (used in validation and UI)
  const isVendorLite = vendorType === "vendor_lite";

  const validateForm = (): boolean => {
    // Clear all previous validation errors first
    clearErrors();

    const newErrors: Record<string, string> = {};

    const isIndia = formData.country === "IN";

    // Base required fields (always required)
    const baseRequiredFields: (keyof VendorEntity)[] = [
      "vendor_legal_name",
      "vendor_legal_entity",
      "type_of_vendor",
      "country",
      "payment_terms",
      "currency_id",
      "gst_category",
      "gst_treatment",
    ];

    // Vendor Pro required fields (additional to base)
    // Note: primary_contact_email is optional for vendor_lite, required for vendor_pro
    if (!isVendorLite) {
      baseRequiredFields.push(
        "business_category",
        "registered_office_address",
        "city",
        "postal_code",
        "primary_contact_name",
        "primary_contact_phone",
        "primary_contact_email",
        "beneficiary_name",
        "bank_account_number",
        "bank_name",
        "branch",
        "ifsc_swift_code",
      );
    }

    baseRequiredFields.forEach((field) => {
      if (
        formData[field] === undefined ||
        (typeof formData[field] === "string" &&
          formData[field]!.toString().trim() === "")
      ) {
        newErrors[field] = `${getFieldName(field)} is required`;
      }
    });

    // State is mandatory for India
    if (isIndia) {
      if (!formData.state || formData.state.trim() === "") {
        newErrors.state = `${getFieldName("state")} is required for India`;
      }
    }

    // PAN is mandatory only for India
    if (isIndia) {
      if (!formData.pan_tax_id || formData.pan_tax_id.trim() === "") {
        newErrors.pan_tax_id = `${getFieldName(
          "pan_tax_id",
        )} is required for India`;
      }
    }

    // MSME is required for India (only for vendor_pro)
    if (isIndia && !isVendorLite) {
      if (!formData.msme_flag || formData.msme_flag.trim() === "") {
        newErrors.msme_flag = `${getFieldName(
          "msme_flag",
        )} is required for India`;
      }
    } else {
      // For non-India countries, MSME should always be "no"
      // This is handled automatically in handleInputChange, but we ensure it here too
      if (formData.msme_flag !== "N") {
        // Silently ignore this - it will be corrected by the form
      }
    }

    // CIN validation only for specific entity types
    // const cinRequiredEntities = [
    //   "llp",
    //   "private_limited",
    //   "public_limited",
    //   "opc",
    //   "section_8",
    // ];
    // if (
    //   formData.vendor_legal_entity &&
    //   cinRequiredEntities.includes(formData.vendor_legal_entity)
    // ) {
    //   if (
    //     !formData.company_registration_number ||
    //     formData.company_registration_number.trim() === ""
    //   ) {
    //     newErrors.company_registration_number = `${getFieldName(
    //       "company_registration_number"
    //     )} is required for ${getFieldName("vendor_legal_entity")}`;
    //   }
    // }

    // MSME validations
    if (formData.msme_flag === "Y") {
      // Udyam Number is mandatory if MSME is Yes
      if (!formData.udyam_number || formData.udyam_number.trim() === "") {
        newErrors.udyam_number = `${getFieldName(
          "udyam_number",
        )} is required when MSME is Yes`;
      }
    }

    // GST validations (gst_category & gst_treatment are always required; GSTIN rules are India-specific)
    if (isIndia) {
      // GSTIN is mandatory if GST Treatment is Business GST
      if (formData.gst_treatment === "business_gst") {
        if (!formData.gstin || formData.gstin.trim() === "") {
          newErrors.gstin = `${getFieldName(
            "gstin",
          )} is required when GST Treatment is Business GST`;
        } else if (
          gstinVerification.verificationRequired &&
          !gstinVerification.isVerified
        ) {
          newErrors.gstin = "Please verify the GSTIN before saving.";
        } else if (
          gstinVerification.isVerified &&
          gstinVerification.nameMismatch &&
          !gstinVerification.nameMatchConfirmed
        ) {
          newErrors.gstin =
            "GSTIN does not match Vendor Name. Please confirm or update.";
        }
      }

      // Non-business_gst with GSTIN
      if (
        formData.gst_treatment !== "business_gst" &&
        formData.gstin &&
        formData.gstin.trim() !== ""
      ) {
        newErrors.gstin =
          "GST Number should only be entered for Business GST entities in India.";
      }
    }

    // TDS validations for India
    if (isIndia) {
      // TDS Applicable is mandatory for India
      if (!formData.tds_applicable || formData.tds_applicable.trim() === "") {
        newErrors.tds_applicable = `TDS Applicable is required for India`;
      }

      // TDS Tax Rate is mandatory for India when TDS is applicable
      if (formData.tds_applicable === "Y") {
        if (!formData.tds_tax_id) {
          newErrors.tds_tax_id = `TDS Tax Rate is required for India when TDS is applicable`;
        }
      }
    }

    // Phone validation
    if (
      formData.primary_contact_phone &&
      !/^[+]?[1-9][\d]{0,15}$/.test(
        formData.primary_contact_phone.replace(/\s/g, ""),
      )
    ) {
      newErrors.primary_contact_phone = "Please enter a valid phone number";
    }

    // GSTIN validation (basic format check)
    if (
      formData.gstin &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.gstin,
      )
    ) {
      newErrors.gstin =
        "Please enter a valid GSTIN format (e.g., 27ABCDE1234F1Z5)";
    }

    // PAN validation (basic format check)
    if (
      formData.pan_tax_id &&
      !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_tax_id)
    ) {
      newErrors.pan_tax_id =
        "Please enter a valid PAN format (e.g., ABCDE1234F)";
    }

    // IFSC validation (basic format check)
    if (
      formData.ifsc_swift_code &&
      !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifsc_swift_code)
    ) {
      newErrors.ifsc_swift_code =
        "Please enter a valid IFSC code format (e.g., SBIN0001234)";
    }

    // Payment terms validation
    if (
      formData.payment_terms !== undefined &&
      formData.payment_terms !== null
    ) {
      const paymentTerms = Number(formData.payment_terms);

      if (isNaN(paymentTerms) || paymentTerms < 0) {
        newErrors.payment_terms = "Payment terms must be a positive number";
      } else if (formData.msme_flag === "Y" && paymentTerms > 45) {
        newErrors.payment_terms =
          "Payment terms cannot exceed 45 days for MSME vendors";
      } else if (formData.msme_flag !== "Y" && paymentTerms > 99) {
        newErrors.payment_terms = "Payment terms cannot exceed 99 days";
      }
    }

    // Set all validation errors at once (after clearing)
    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleCancel = () => {
    if (manualBillReturn) {
      navigate(manualBillReturn.createManualReturnPath, {
        state: { billDraft: manualBillReturn.billDraft },
      });
      return;
    }
    navigate(-1);
  };

  const handleReviewSuccess = () => {
    // ReviewRequestModal already showed success toast
    navigate("/dashboard/masters?tab=vendors");
  };

  const handleSave = async () => {
    // Run validation first
    if (!validateForm()) {
      // ErrorBanner will display all validation errors and auto-scroll
      return;
    }

    // Check if review is required
    if (needsReview) {
      // Enrich formData with currency and TDS names for display
      const selectedCurrency = currencies.find(
        (c) => c.currency_id === formData.currency_id,
      );
      const selectedTdsRate = tdsRates.find(
        (rate) => rate.tds_tax_id === formData.tds_tax_id?.toString(),
      );
      const enrichedFormData = {
        ...formData,
        currency_name: selectedCurrency?.currency_name,
        currency_code: selectedCurrency?.currency_code,
        tds_name: selectedTdsRate?.tds_tax_name,
      };
      setPendingFormData(enrichedFormData);
      setShowReviewModal(true);
      return;
    }

    // Direct save (existing logic)
    try {
      // Get currency code from selected currency
      const selectedCurrency = currencies.find(
        (c) => c.currency_id === formData.currency_id,
      );
      const formDataWithCurrency = {
        ...formData,
        currency: selectedCurrency?.currency_code,
        currency_code: selectedCurrency?.currency_code,
      };

      const vendorEntity = await createVendor(formDataWithCurrency);

      if (vendorEntity) {
        if (vendorContextData.final_context || vendorContextData.raw_input) {
          try {
            await saveVendorContext(
              Number(vendorEntity.vendorid),
              vendorContextData,
            );
          } catch (ctxError) {
            console.warn("Vendor saved but context save failed:", ctxError);
          }
        }

        if (manualBillReturn) {
          const company = getCompany();
          const destState = getStateAbbreviation(company?.state || "") || "";
          const applyVendorPatch = buildManualBillVendorPatch(
            {
              zoho_vendorid: vendorEntity.zoho_vendorid,
              vendorid: vendorEntity.vendorid,
              vendor_legal_name: vendorEntity.vendor_legal_name,
              gstin: vendorEntity.gstin,
              state: vendorEntity.state,
              country: vendorEntity.country,
              registered_office_address: vendorEntity.registered_office_address,
              gst_treatment: vendorEntity.gst_treatment ?? undefined,
              gst_category: vendorEntity.gst_category ?? undefined,
            },
            destState,
          );
          showSuccess("Vendor created successfully");
          navigate(manualBillReturn.createManualReturnPath, {
            state: {
              billDraft: manualBillReturn.billDraft,
              applyVendorPatch,
            },
          });
          return;
        }

        const vendorData = {
          vendor_id: vendorEntity.vendorid,
          zoho_vendorid: vendorEntity.zoho_vendorid,
          source_of_supply: vendorEntity.state,
          vendor_legal_name: vendorEntity.vendor_legal_name,
          type_of_vendor: vendorEntity.type_of_vendor,
          gstin: vendorEntity.gstin,
          currency_id: vendorEntity.currency_id,
          currency_code: selectedCurrency?.currency_code,
          gst_treatment: vendorEntity.gst_treatment,
          gst_category: vendorEntity.gst_category,
          registered_office_address: vendorEntity.registered_office_address,
          address: vendorEntity.registered_office_address,
          payment_terms: vendorEntity.payment_terms,
          state: vendorEntity.state,
          country: vendorEntity.country,
        };

        console.log("🎯 AddNewParty: Storing vendor data for auto-select");

        sessionStorage.setItem(
          "autoSelectVendor",
          JSON.stringify({
            vendor: vendorData,
            action: "created",
            timestamp: Date.now(),
          }),
        );
      }

      showSuccess("Vendor created successfully");

      navigate(-1);
    } catch (error) {
      console.error("Error creating vendor:", error);
      // Display all API errors in ErrorBanner as global errors
      setApiErrors(error);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start bg-default-background">
        <div className="flex w-full items-center justify-between border-b border-solid border-neutral-border px-6 py-6">
          <div className="flex items-center gap-2">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Add New Party
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="neutral-tertiary"
              onClick={handleCancel}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || !canCreate}
              loading={isCreating}
            >
              {isCreating ? "Saving..." : "Save Party"}
            </Button>
          </div>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start overflow-auto">
          <div className="flex w-full flex-col items-start gap-8 px-6 py-6">
            {/* Error Display */}
            {/* {submitError && (
              <ErrorDisplay
                error={submitError}
                onRetry={handleSave}
                onLogin={() => (window.location.href = "/login")}
                customActions={[
                  {
                    label: "Retry Save",
                    action: handleSave,
                    variant: "neutral-tertiary",
                  },
                ]}
              />
            )} */}

            {/* Display validation errors with ErrorBanner component */}
            <div className="w-full pb-2">
              <ErrorBanner
                errors={errors}
                title="Please fix the following errors:"
                autoScrollOnMount={true}
                context="vendor"
              />
            </div>

            {/* Vendor Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Vendor Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label="Vendor Legal Name*"
                  helpText={errors.vendor_legal_name}
                  error={!!errors.vendor_legal_name}
                >
                  <TextField.Input
                    placeholder="Enter legal name"
                    maxLength={1000}
                    value={formData.vendor_legal_name || ""}
                    onChange={(event) =>
                      handleInputChange("vendor_legal_name", event.target.value)
                    }
                    data-field="vendor_legal_name"
                  />
                </TextField>
                <Select
                  label="Vendor Legal Entity*"
                  placeholder="Select legal entity"
                  helpText={errors.vendor_legal_entity}
                  error={!!errors.vendor_legal_entity}
                  value={formData.vendor_legal_entity}
                  onValueChange={(value) =>
                    handleInputChange("vendor_legal_entity", value)
                  }
                >
                  <Select.Item value="sole_proprietorship">
                    Sole Proprietorship
                  </Select.Item>
                  <Select.Item value="partnership_firm">
                    Partnership Firm (under the Indian Partnership Act, 1932)
                  </Select.Item>
                  <Select.Item value="huf">
                    Hindu Undivided Family (HUF)
                  </Select.Item>
                  <Select.Item value="llp">
                    Limited Liability Partnership
                  </Select.Item>
                  <Select.Item value="private_limited">
                    Private Limited Company
                  </Select.Item>
                  <Select.Item value="public_limited">
                    Public Limited Company
                  </Select.Item>
                  <Select.Item value="opc">One Person Company</Select.Item>
                  <Select.Item value="section_8">Section 8 Company</Select.Item>
                  <Select.Item value="other">
                    Specialised/Other Entities
                  </Select.Item>
                </Select>
                <Select
                  label="Type of Vendor*"
                  placeholder="Select vendor type"
                  helpText={errors.type_of_vendor}
                  error={!!errors.type_of_vendor}
                  value={formData.type_of_vendor}
                  onValueChange={(value) =>
                    handleInputChange("type_of_vendor", value)
                  }
                >
                  <Select.Item value="supplier">Supplier</Select.Item>
                  <Select.Item value="service">Service</Select.Item>
                  <Select.Item value="contractor">Contractor</Select.Item>
                </Select>
                <Select
                  label={
                    isVendorLite ? "Business Category" : "Business Category*"
                  }
                  placeholder="Select category"
                  helpText={errors.business_category}
                  error={!!errors.business_category}
                  value={formData.business_category}
                  onValueChange={(value) =>
                    handleInputChange("business_category", value)
                  }
                >
                  <Select.Item value="manufacturer">Manufacturer</Select.Item>
                  <Select.Item value="service">Service Provider</Select.Item>
                  <Select.Item value="retailer">Retailer</Select.Item>
                  <Select.Item value="wholesaler">Wholesaler</Select.Item>
                </Select>
                <TextField label="Trading Name" helpText="">
                  <TextField.Input
                    placeholder="Enter trading name"
                    value={formData.trading_name || ""}
                    onChange={(event) =>
                      handleInputChange("trading_name", event.target.value)
                    }
                  />
                </TextField>
                <TextField label="Group Parent Entity" helpText="">
                  <TextField.Input
                    placeholder="Enter parent entity"
                    value={formData.group_parent_entity || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "group_parent_entity",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <Select
                  label="Vendor Classification"
                  placeholder="Select classification"
                  helpText=""
                  value={formData.vendor_classification}
                  onValueChange={(value) =>
                    handleInputChange("vendor_classification", value)
                  }
                >
                  <Select.Item value="critical">Critical</Select.Item>
                  <Select.Item value="non-critical">Non-Critical</Select.Item>
                </Select>
              </div>
            </div>

            {/* Taxation */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Taxation
              </span>

              {/* GST Subsection */}
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-heading-4 font-heading-4 text-default-font">
                  GST
                </span>
                <div className="w-full items-start gap-4 grid grid-cols-2">
                  <Select
                    label="GST Treatment*"
                    placeholder="Select GST treatment"
                    helpText={
                      errors.gst_treatment ||
                      (formData.country !== "IN"
                        ? "Use Overseas for non-India; category often defaults to RCM"
                        : "")
                    }
                    error={!!errors.gst_treatment}
                    value={formData.gst_treatment}
                    onValueChange={(value) =>
                      handleInputChange("gst_treatment", value)
                    }
                    data-field="gst_treatment"
                  >
                    <Select.Item value="business_gst">Business GST</Select.Item>
                    <Select.Item value="business_none">
                      Business None
                    </Select.Item>
                    <Select.Item value="overseas">Overseas</Select.Item>
                    <Select.Item value="consumer">Consumer</Select.Item>
                  </Select>
                  <GstinVerificationField
                    gstin={formData.gstin || ""}
                    gstTreatment={formData.gst_treatment || ""}
                    entityName={formData.vendor_legal_name || ""}
                    entityType="vendor"
                    error={errors.gstin}
                    disabled={!(formData.gst_treatment === "business_gst")}
                    verificationStatus={gstinVerification.status}
                    verificationData={gstinVerification.verificationData}
                    verificationRequired={
                      gstinVerification.verificationRequired
                    }
                    nameMismatch={gstinVerification.nameMismatch}
                    nameMatchConfirmed={gstinVerification.nameMatchConfirmed}
                    errorMessage={gstinVerification.errorMessage}
                    onGstinChange={(value) => handleInputChange("gstin", value)}
                    onVerify={() =>
                      gstinVerification.verify(formData.gstin || "")
                    }
                    onConfirmNameMatch={() => {
                      gstinVerification.confirmNameMatch();
                      if (gstinVerification.verificationData?.legal_name) {
                        handleInputChange(
                          "vendor_legal_name",
                          gstinVerification.verificationData.legal_name,
                        );
                      }
                    }}
                  />
                  <Select
                    label="GST Category*"
                    placeholder="Select GST category"
                    helpText={
                      errors.gst_category ||
                      (formData.country !== "IN" &&
                      formData.gst_treatment === "overseas"
                        ? "Often RCM for overseas"
                        : "")
                    }
                    error={!!errors.gst_category}
                    value={formData.gst_category}
                    onValueChange={(value) =>
                      handleInputChange("gst_category", value)
                    }
                    disabled={
                      formData.country !== "IN" &&
                      formData.gst_treatment === "overseas" &&
                      formData.type_of_vendor === "service"
                    }
                  >
                    <Select.Item value="regular">Regular</Select.Item>
                    <Select.Item value="exempt">Exempt</Select.Item>
                    <Select.Item value="unregistered">Unregistered</Select.Item>
                    <Select.Item value="rcm">RCM</Select.Item>
                  </Select>

                  <TextField label="Exemption Certificates" helpText="">
                    <TextField.Input
                      placeholder="Enter certificates"
                      value={formData.exemption_certificates || ""}
                      onChange={(event) =>
                        handleInputChange(
                          "exemption_certificates",
                          event.target.value,
                        )
                      }
                    />
                  </TextField>
                  <TextField label="Typical HSN / SAC Codes" helpText="">
                    <TextField.Input
                      placeholder="Enter codes"
                      value={formData.typical_hsn_sac_codes || ""}
                      onChange={(event) =>
                        handleInputChange(
                          "typical_hsn_sac_codes",
                          event.target.value,
                        )
                      }
                    />
                  </TextField>
                </div>
              </div>

              {/* TDS Subsection */}
              <div className="flex w-full flex-col items-start gap-4">
                <span className="text-heading-4 font-heading-4 text-default-font">
                  TDS
                </span>
                <div className="w-full items-start gap-4 grid grid-cols-2">
                  <Select
                    label="TDS Applicable"
                    placeholder="Select TDS status"
                    helpText={
                      errors.tds_applicable ||
                      (formData.country === "IN" ? "Required for India" : "")
                    }
                    error={!!errors.tds_applicable}
                    value={formData.tds_applicable}
                    disabled={tdsRatesLoading}
                    onValueChange={(value) =>
                      handleInputChange("tds_applicable", value)
                    }
                  >
                    <Select.Item value="Y">Yes</Select.Item>
                    <Select.Item value="N">No</Select.Item>
                  </Select>
                  <Select
                    label="TDS Tax Rate"
                    placeholder={
                      tdsRatesLoading ? "Loading..." : "Select tax rate"
                    }
                    helpText={
                      errors.tds_tax_id ||
                      tdsRatesError ||
                      (formData.country === "IN" &&
                      formData.tds_applicable === "Y"
                        ? "Required for India when TDS is applicable"
                        : formData.tds_applicable === "N"
                          ? "Not applicable when TDS Applicable is No"
                          : "")
                    }
                    error={!!errors.tds_tax_id || !!tdsRatesError}
                    disabled={
                      tdsRatesLoading || formData.tds_applicable === "N"
                    }
                    value={formData.tds_tax_id?.toString() ?? ""}
                    onValueChange={(value) =>
                      handleInputChange("tds_tax_id", value)
                    }
                  >
                    {getFilteredTDSRates().map((rate) => (
                      <Select.Item key={rate.id} value={rate.tds_tax_id}>
                        {formatTdsRateLabel(
                          rate.tds_tax_name,
                          rate.tds_tax_percentage,
                          rate.is_expired ?? true,
                        )}
                      </Select.Item>
                    ))}
                  </Select>
                </div>
              </div>
            </div>

            {/* Compliance & Registration */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Compliance &amp; Registration
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label={formData.country === "IN" ? "PAN ID*" : "PAN ID"}
                  helpText={
                    errors.pan_tax_id ||
                    (formData.country === "IN" ? "Required for India" : "")
                  }
                  error={!!errors.pan_tax_id}
                >
                  <TextField.Input
                    placeholder="Enter PAN"
                    maxLength={10}
                    value={formData.pan_tax_id || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "pan_tax_id",
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, ""),
                      )
                    }
                  />
                </TextField>

                <Select
                  label={
                    isVendorLite
                      ? "MSME Flag"
                      : formData.country === "IN"
                        ? "MSME Flag*"
                        : "MSME Flag"
                  }
                  placeholder="Select MSME status"
                  helpText={
                    errors.msme_flag ||
                    (formData.country !== "IN"
                      ? "MSME is only applicable for India"
                      : formData.country === "IN" && !isVendorLite
                        ? "Required for India"
                        : "")
                  }
                  error={!!errors.msme_flag}
                  value={formData.msme_flag}
                  onValueChange={(value) =>
                    handleInputChange("msme_flag", value)
                  }
                  disabled={formData.country !== "IN"}
                >
                  <Select.Item value="Y">Yes</Select.Item>
                  <Select.Item value="N">No</Select.Item>
                </Select>
                <TextField
                  label="Company Registration Number (CIN)"
                  helpText={
                    errors.company_registration_number ||
                    ([
                      "llp",
                      "private_limited",
                      "public_limited",
                      "opc",
                      "section_8",
                    ].includes(formData.vendor_legal_entity || "")
                      ? "Required for this entity type"
                      : "")
                  }
                  error={!!errors.company_registration_number}
                >
                  <TextField.Input
                    placeholder="Enter registration number"
                    value={formData.company_registration_number || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "company_registration_number",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField label="Import Export Code" helpText="">
                  <TextField.Input
                    placeholder="Enter IE code"
                    value={formData.import_export_code || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "import_export_code",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField label="ISO Certifications" helpText="">
                  <TextField.Input
                    placeholder="Enter certifications"
                    value={formData.iso_certifications || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "iso_certifications",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField label="Regulatory Licenses" helpText="">
                  <TextField.Input
                    placeholder="Enter licenses"
                    value={formData.regulatory_licenses || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "regulatory_licenses",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField
                  label="Udyam Number"
                  helpText={
                    errors.udyam_number ||
                    (formData.msme_flag === "Y"
                      ? "Required for MSME vendors"
                      : formData.msme_flag === "N"
                        ? "Only applicable when MSME is Yes"
                        : "")
                  }
                  error={!!errors.udyam_number}
                >
                  <TextField.Input
                    placeholder="Enter Udyam number"
                    maxLength={19}
                    value={formData.udyam_number || ""}
                    onChange={(event) =>
                      handleInputChange("udyam_number", event.target.value)
                    }
                    disabled={formData.msme_flag !== "Y"}
                  />
                </TextField>
              </div>
            </div>

            {/* Address Details */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Address Details
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  className="col-span-2"
                  label={
                    isVendorLite
                      ? "Registered Office Address"
                      : "Registered Office Address*"
                  }
                  helpText={errors.registered_office_address}
                  error={!!errors.registered_office_address}
                >
                  <TextField.Input
                    placeholder="Enter registered address"
                    value={formData.registered_office_address || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "registered_office_address",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Country*
                  </span>
                  <SearchableDropdown
                    items={COUNTRIES}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                      >
                        {formData.country
                          ? findCountryByCode(formData.country)?.name ||
                            "Select country"
                          : "Select country"}
                      </Button>
                    }
                    getSearchableText={(country) => country.name}
                    renderItem={(country) => country.name}
                    getItemKey={(country) => country.code}
                    onSelect={(country) =>
                      handleInputChange("country", country.code)
                    }
                    selectedKey={formData.country}
                    searchPlaceholder="Search countries..."
                    emptyMessage="No countries available"
                    noResultsMessage="No countries match your search"
                  />
                  {errors.country && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.country}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    {formData.country === "IN" ? "State*" : "State"}
                  </span>
                  <SearchableDropdown
                    items={INDIAN_STATES}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                        disabled={formData.country !== "IN"}
                      >
                        {formData.country === "IN" && formData.state
                          ? findIndianStateByCode(formData.state)?.name ||
                            "Select state"
                          : "Select state"}
                      </Button>
                    }
                    getSearchableText={(state) => state.name}
                    renderItem={(state) => state.name}
                    getItemKey={(state) => state.code}
                    onSelect={(state) => handleInputChange("state", state.code)}
                    selectedKey={formData.state}
                    searchPlaceholder="Search states..."
                    emptyMessage="No states available"
                    noResultsMessage="No states match your search"
                    disabled={formData.country !== "IN"}
                  />
                  {(errors.state ||
                    (formData.country === "IN" && "Required for India")) && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.state ||
                        (formData.country === "IN" ? "Required for India" : "")}
                    </span>
                  )}
                </div>
                <TextField
                  label={isVendorLite ? "City" : "City*"}
                  helpText={errors.city}
                  error={!!errors.city}
                >
                  <TextField.Input
                    placeholder="Enter city"
                    maxLength={100}
                    value={formData.city || ""}
                    onChange={(event) =>
                      handleInputChange("city", event.target.value)
                    }
                  />
                </TextField>
                <TextField
                  label={isVendorLite ? "Postal Code" : "Postal Code*"}
                  helpText={errors.postal_code}
                  error={!!errors.postal_code}
                >
                  <TextField.Input
                    placeholder="Enter postal code"
                    maxLength={20}
                    value={formData.postal_code || ""}
                    onChange={(event) =>
                      handleInputChange("postal_code", event.target.value)
                    }
                  />
                </TextField>
                <TextField
                  className="col-span-2"
                  label="Correspondence Address"
                  helpText=""
                >
                  <TextField.Input
                    placeholder="Enter correspondence address"
                    value={formData.correspondence_address || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "correspondence_address",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField
                  className="col-span-2"
                  label="Beneficiary Address"
                  helpText=""
                >
                  <TextField.Input
                    placeholder="Enter beneficiary address"
                    value={formData.beneficiary_address || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "beneficiary_address",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
              </div>
            </div>

            {/* Contact Information */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Contact Information
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label={
                    isVendorLite
                      ? "Primary Contact Name"
                      : "Primary Contact Name*"
                  }
                  helpText={errors.primary_contact_name}
                  error={!!errors.primary_contact_name}
                >
                  <TextField.Input
                    placeholder="Enter contact name"
                    maxLength={255}
                    value={formData.primary_contact_name || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "primary_contact_name",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField
                  label={
                    isVendorLite
                      ? "Primary Contact Phone"
                      : "Primary Contact Phone*"
                  }
                  helpText={errors.primary_contact_phone}
                  error={!!errors.primary_contact_phone}
                >
                  <TextField.Input
                    placeholder="Enter phone number"
                    maxLength={20}
                    value={formData.primary_contact_phone || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "primary_contact_phone",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField
                  label={
                    isVendorLite
                      ? "Primary Contact Email"
                      : "Primary Contact Email*"
                  }
                  helpText={errors.primary_contact_email}
                  error={!!errors.primary_contact_email}
                >
                  <TextField.Input
                    placeholder="Enter email"
                    maxLength={255}
                    value={formData.primary_contact_email || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "primary_contact_email",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField label="Alternate Phone" helpText="">
                  <TextField.Input
                    placeholder="Enter alternate phone"
                    maxLength={20}
                    value={formData.alternate_contact || ""}
                    onChange={(event) =>
                      handleInputChange("alternate_contact", event.target.value)
                    }
                  />
                </TextField>
                <TextField label="Alternate Email" helpText="">
                  <TextField.Input
                    placeholder="Enter alternate email"
                    maxLength={255}
                    value={formData.escalation_contact || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "escalation_contact",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField label="Website" helpText="">
                  <TextField.Input
                    placeholder="Enter website"
                    value={formData.website || ""}
                    onChange={(event) =>
                      handleInputChange("website", event.target.value)
                    }
                  />
                </TextField>
              </div>
            </div>

            {/* Banking & Payment */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Banking &amp; Payment
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <TextField
                  label={
                    isVendorLite ? "Beneficiary Name" : "Beneficiary Name*"
                  }
                  helpText={errors.beneficiary_name}
                  error={!!errors.beneficiary_name}
                >
                  <TextField.Input
                    placeholder="Enter beneficiary name"
                    value={formData.beneficiary_name || ""}
                    onChange={(event) =>
                      handleInputChange("beneficiary_name", event.target.value)
                    }
                  />
                </TextField>
                <TextField
                  label={
                    isVendorLite
                      ? "Bank Account Number"
                      : "Bank Account Number*"
                  }
                  helpText={errors.bank_account_number}
                  error={!!errors.bank_account_number}
                >
                  <TextField.Input
                    placeholder="Enter account number"
                    value={formData.bank_account_number || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "bank_account_number",
                        event.target.value,
                      )
                    }
                  />
                </TextField>
                <TextField
                  label={isVendorLite ? "Bank Name" : "Bank Name*"}
                  helpText={errors.bank_name}
                  error={!!errors.bank_name}
                >
                  <TextField.Input
                    placeholder="Enter bank name"
                    value={formData.bank_name || ""}
                    onChange={(event) =>
                      handleInputChange("bank_name", event.target.value)
                    }
                  />
                </TextField>
                <TextField
                  label={isVendorLite ? "Branch" : "Branch*"}
                  helpText={errors.branch}
                  error={!!errors.branch}
                >
                  <TextField.Input
                    placeholder="Enter branch"
                    value={formData.branch || ""}
                    onChange={(event) =>
                      handleInputChange("branch", event.target.value)
                    }
                  />
                </TextField>
                <TextField
                  label={
                    isVendorLite ? "IFSC / SWIFT Code" : "IFSC / SWIFT Code*"
                  }
                  helpText={errors.ifsc_swift_code}
                  error={!!errors.ifsc_swift_code}
                >
                  <TextField.Input
                    placeholder="Enter IFSC/SWIFT code"
                    maxLength={11}
                    value={formData.ifsc_swift_code || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "ifsc_swift_code",
                        event.target.value
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, ""),
                      )
                    }
                  />
                </TextField>
                <Select
                  label="Preferred Payment Method"
                  placeholder="Select payment method"
                  helpText=""
                  value={formData.preferred_payment_method}
                  onValueChange={(value) =>
                    handleInputChange("preferred_payment_method", value)
                  }
                >
                  <Select.Item value="bank">Bank</Select.Item>
                  <Select.Item value="cheque">Cheque</Select.Item>
                </Select>
                <TextField label="Advance Security Deposit" helpText="">
                  <TextField.Input
                    placeholder="Enter deposit amount"
                    value={formData.advance_security_deposit?.toString() || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "advance_security_deposit",
                        parseFloat(event.target.value) || 0,
                      )
                    }
                  />
                </TextField>
                <TextField
                  label="Payment Terms (days)*"
                  helpText={
                    errors.payment_terms ||
                    (formData.msme_flag === "Y"
                      ? "Max 45 days for MSME vendors"
                      : "Max 99 days")
                  }
                  error={!!errors.payment_terms}
                >
                  <TextField.Input
                    type="number"
                    placeholder="Enter payment terms in days"
                    value={formData.payment_terms?.toString() || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "payment_terms",
                        parseInt(event.target.value) || 0,
                      )
                    }
                  />
                </TextField>
                <div className="flex flex-col gap-1">
                  <span className="text-caption-bold font-caption-bold text-default-font">
                    Currency
                  </span>
                  <SearchableDropdown
                    items={currencies}
                    loading={currenciesLoading}
                    trigger={
                      <Button
                        variant="neutral-secondary"
                        className="w-full justify-between"
                        iconRight={<FeatherChevronDown />}
                        disabled={currenciesLoading}
                      >
                        {currenciesLoading
                          ? "Loading currencies..."
                          : formData.currency_id
                            ? currencies.find(
                                (c) => c.currency_id === formData.currency_id,
                              )?.currency_name || "Select currency"
                            : "Select currency"}
                      </Button>
                    }
                    getSearchableText={(currency) => currency.currency_name}
                    renderItem={(currency) => currency.currency_name}
                    getItemKey={(currency) => currency.currency_id}
                    onSelect={(currency) =>
                      handleInputChange("currency_id", currency.currency_id)
                    }
                    selectedKey={formData.currency_id}
                    searchPlaceholder="Search currencies..."
                    emptyMessage="No currencies available"
                    noResultsMessage="No currencies match your search"
                  />
                  {(errors.currency_id || currenciesError) && (
                    <span className="text-caption font-caption text-error-700">
                      {errors.currency_id || currenciesError}
                    </span>
                  )}
                </div>
                <TextField label="Credit Limit" helpText="">
                  <TextField.Input
                    placeholder="Enter credit limit"
                    value={formData.credit_limit?.toString() || ""}
                    onChange={(event) =>
                      handleInputChange(
                        "credit_limit",
                        parseFloat(event.target.value) || 0,
                      )
                    }
                  />
                </TextField>
              </div>
            </div>

            {/* Risk & Assessment */}
            <div className="flex w-full flex-col items-start gap-6">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Risk &amp; Assessment
              </span>
              <div className="w-full items-start gap-4 grid grid-cols-2">
                <Select
                  label="Risk Rating"
                  placeholder="Select risk rating"
                  helpText=""
                  value={formData.risk_rating}
                  onValueChange={(value) =>
                    handleInputChange("risk_rating", value)
                  }
                >
                  <Select.Item value="low">low</Select.Item>
                  <Select.Item value="medium">medium</Select.Item>
                  <Select.Item value="high">high</Select.Item>
                </Select>
              </div>
            </div>

            {/* GL Classification Context */}
            <VendorContextSection
              vendorName={formData.vendor_legal_name || ""}
              onContextChange={setVendorContextData}
            />
          </div>
        </div>
      </div>

      {/* Review Request Modal */}
      <ReviewRequestModal
        open={showReviewModal}
        onOpenChange={setShowReviewModal}
        moduleCode={MODULE_CODES.VENDOR}
        actionCode={ACTION_CODES.CREATE}
        entityType="vendor"
        entityId={0}
        onSuccess={handleReviewSuccess}
        changeDetails={{ raw_json: pendingFormData, field_changes: [] }}
        skipNavigation={true}
      />

      {/* GSTIN Address Prefill Dialog */}
      <GSTINPrefillDialog
        isOpen={showPrefillDialog}
        entityType="vendor"
        fields={buildVendorGstinPrefillFields(
          formData,
          gstinVerification.verificationData,
          formData.gstin || "",
          currencies,
        )}
        onApplyAll={handleApplyGstinPrefill}
        onClose={() => setShowPrefillDialog(false)}
      />
    </DefaultPageLayout>
  );
};

export default AddNewPartyPage;
